import { PieChart, PieChartProps } from '@opd/g2plot-react';
import { Card, Flex, Tooltip } from 'antd';
import { InfoOutline } from 'icons-new';
import { useTranslation } from 'react-i18next';
import { isMobileScreenSize } from 'utils/helpers';
import usePipeReviewWidgetQuery from '../hooks/usePipeReviewWidgetQuery';

const chartColors = ['#95DE64', '#FF7875', '#FFC069', '#69B1FF'];
const widgetProperties = [
  { title: 'Drying Event 1', key: 'dryingEvent1' },
  { title: 'Drying Event 2', key: 'dryingEvent2' },
];

type WidgetKey = 'dryingEvent1' | 'dryingEvent2';

const PipeReviewDashboardWidget = ({
  seasonIndex,
}: {
  seasonIndex?: string;
}) => {
  const { t } = useTranslation();
  const { data: { data: dashboard } = {} } =
    usePipeReviewWidgetQuery(seasonIndex);

  const getChartData = (dryingEvent: WidgetKey) => [
    {
      name: 'Approved',
      value: dashboard?.[dryingEvent]?.countApproved || 0,
    },
    {
      name: 'Rejected',
      value: dashboard?.[dryingEvent]?.countRejected || 0,
    },
    {
      name: 'In Review',
      value: dashboard?.[dryingEvent]?.countInReview || 0,
    },
    {
      name: 'Overdue',
      value: dashboard?.[dryingEvent]?.countOverdue || 0,
    },
  ];

  const pieChartConfig: Omit<PieChartProps, 'data'> = {
    angleField: 'value',
    colorField: 'name',
    radius: 1,
    innerRadius: 0.6,
    animation: false,
    width: isMobileScreenSize ? 140 : 180,
    height: isMobileScreenSize ? 140 : 180,
    color: ['#95DE64', '#FF7875', '#FFC069', '#69B1FF'],
    legend: false,
    label: false,
  };

  return (
    <Flex gap={16} className="pipe-review-dashboard flex-col-sm">
      {widgetProperties.map((item) => (
        <Card key={item.key} className="pipe-review-dashboard-item">
          <Flex vertical gap={20}>
            <span className="text-strong">{item.title}</span>

            <Flex gap={isMobileScreenSize ? 12 : 24}>
              <PieChart
                data={getChartData(item.key as WidgetKey)}
                statistic={{
                  title: false,
                  content: {
                    content:
                      dashboard?.[
                        item.key as WidgetKey
                      ]?.countResult?.toString() ?? '0',
                    style: {
                      fontSize: '24px',
                      color: '#545454',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                    },
                  },
                }}
                {...pieChartConfig}
              />
              <Flex
                vertical
                gap={16}
                className="label-container text-large text-base-sm"
              >
                {getChartData(item.key as WidgetKey)?.map((data, index) => (
                  <Flex justify="space-between" align="center" key={data.name}>
                    <Flex justify="center" align="center" gap={8}>
                      <div
                        className="dot"
                        style={{
                          background: chartColors[index],
                        }}
                      />
                      {data.name}
                      {index === 3 && (
                        <Tooltip title={t('pipeReview.unreviewedForOver')}>
                          <InfoOutline className="warning-icon" />
                        </Tooltip>
                      )}
                    </Flex>
                    <span className="text-secondary text-strong">
                      {data.value}
                    </span>
                  </Flex>
                ))}
              </Flex>
            </Flex>
          </Flex>
        </Card>
      ))}
    </Flex>
  );
};

export default PipeReviewDashboardWidget;
